import moment from 'moment-timezone';
import { TIME_ZONE_RELATIONSHIP_ABBREVIATED } from 'src/constants/formOptions';

export const isWithinMessageSendHours = (userMessageSendTimes, userTimezone = 'America/New_York', textingProvider = 'PB') => {
  // If not using CP texting, no send window restrictions
  if (textingProvider !== 'CP') {
    return true;
  }

  // If no settings found or set to inactive, return false
  if (!userMessageSendTimes || !userMessageSendTimes.isActive) {
    return false;
  }

  const {
    useDifferentHoursPerDay,
    weekSendAtAnyTime,
    weekTimeFrom,
    weekTimeTo,
  } = userMessageSendTimes;

  // If using same times for entire week and weekSendAtAnyTime is true
  if (!useDifferentHoursPerDay && weekSendAtAnyTime) {
    return true;
  }

  const day = moment().tz(userTimezone).format('dddd').toLowerCase(); // "monday", etc.

  // If setting hours by/for each day & use custom hours for this day is false (meaning send at any time), return true
  if (useDifferentHoursPerDay && !userMessageSendTimes[`${day}CustomHours`]) {
    return true;
  }

  // Get current time in HH:mm:ss
  const now = moment().tz(userTimezone).format('HH:mm:ss');

  // Choose which window to use
  let from;
  let to;

  if (!useDifferentHoursPerDay) {
    from = weekTimeFrom;
    to = weekTimeTo;
  } else {
    from = userMessageSendTimes[`${day}TimeFrom`];
    to = userMessageSendTimes[`${day}TimeTo`];
  }

  // If both empty → allow any time
  if (!from && !to) {
    return true;
  }

  // If either bound missing, not allowed
  if (!from || !to) {
    return false;
  }

  // Simple lexical compare works for "HH:mm:ss"
  return from <= now && now <= to;
};

// Resolves today's send window in the user's timezone, or null when there is
// no window to state (send-at-any-time, no custom hours for today, or hours
// not set). Shared by getNextSendTimeMessage and getSendHoursRange so the two
// can never disagree about which window applies; the gating below is exactly
// what getNextSendTimeMessage applied on its own before it was extracted.
const resolveTodayWindow = (userMessageSendTimes, userTimezone) => {
  if (!userMessageSendTimes || !userMessageSendTimes.isActive) {
    return null;
  }

  const {
    useDifferentHoursPerDay,
    weekSendAtAnyTime,
    weekTimeFrom,
    weekTimeTo,
  } = userMessageSendTimes;

  // If using same times for entire week and weekSendAtAnyTime is true
  if (!useDifferentHoursPerDay && weekSendAtAnyTime) {
    return null;
  }

  const day = moment().tz(userTimezone).format('dddd').toLowerCase();

  // If setting hours by/for each day & use custom hours for this day is false
  if (useDifferentHoursPerDay && !userMessageSendTimes[`${day}CustomHours`]) {
    return null;
  }

  let from;
  let to;

  if (!useDifferentHoursPerDay) {
    from = weekTimeFrom;
    to = weekTimeTo;
  } else {
    from = userMessageSendTimes[`${day}TimeFrom`];
    to = userMessageSendTimes[`${day}TimeTo`];
  }

  if (!from || !to) {
    return null;
  }

  // If from === to, this day is blocked (no messages will be sent)
  if (from === to) {
    return { isBlocked: true };
  }

  // Parse the times in the user's timezone
  const now = moment().tz(userTimezone);
  const timeFrom = moment.tz(`${now.format('YYYY-MM-DD')} ${from}`, userTimezone).format('h:mm A');
  const timeTo = moment.tz(`${now.format('YYYY-MM-DD')} ${to}`, userTimezone).format('h:mm A');

  // Get timezone abbreviation (ET, CT, MT, PT, etc.)
  const tzAbbr = TIME_ZONE_RELATIONSHIP_ABBREVIATED.find(([tz]) => tz === userTimezone)?.[1] || 'Local Time';

  return { isBlocked: false, range: `${timeFrom} - ${timeTo} ${tzAbbr}` };
};

export const getNextSendTimeMessage = (userMessageSendTimes, userTimezone = 'America/New_York') => {
  const sendWindow = resolveTodayWindow(userMessageSendTimes, userTimezone);

  if (!sendWindow) {
    return '';
  }

  if (sendWindow.isBlocked) {
    return 'Automated messages are blocked today.';
  }

  return `Today's Send Hours: ${sendWindow.range}.`;
};

// The bare window -- "8:30 AM - 9:05 PM CT", no "Today's Send Hours:" prefix
// and no trailing period. Used for the narrow-viewport wording of the
// outside-send-hours banner, where the full sentence wrapped to three lines.
// Returns '' when the day is blocked or there is no window to state, matching
// getNextSendTimeMessage.
export const getSendHoursRange = (userMessageSendTimes, userTimezone = 'America/New_York') => {
  const sendWindow = resolveTodayWindow(userMessageSendTimes, userTimezone);

  if (!sendWindow || sendWindow.isBlocked) {
    return '';
  }

  return sendWindow.range;
};
